import { useEffect, useState } from "react";
import Peer, { type PeerError } from "peerjs";
import { presencePeerIdFromPractitionerRef } from "./teleconsultoriaPeerId";
import {
  probeTeleconsultoriaPeer,
  type TeleconsultoriaPresenceStatus,
} from "./probeTeleconsultoriaPeer";

const REFRESH_MS = 15_000;
/** Se o broker não responder, a lista continua e o status fica desconhecido. */
const OPEN_TIMEOUT_MS = 8_000;

/**
 * Erros do broker. Não incluem `peer-unavailable`, que vale para um
 * profissional só e é tratado em cada tentativa de conexão.
 */
const BROKER_ERROR_TYPES = new Set([
  "network",
  "server-error",
  "socket-error",
  "socket-closed",
  "invalid-key",
  "ssl-unavailable",
  "browser-incompatible",
]);

/**
 * Um único Peer de ID aleatório consulta quem está online.
 * A lista de profissionais continua vindo do Schedule FHIR.
 */
export function useTeleconsultoriaPresence(references: string[]) {
  const referenceKey = references.join("|");
  const [statusByReference, setStatusByReference] = useState<
    Record<string, TeleconsultoriaPresenceStatus>
  >({});

  useEffect(() => {
    const refs = referenceKey ? referenceKey.split("|") : [];
    if (!refs.length) {
      setStatusByReference({});
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let brokerFailed = false;
    let refreshTimer: number | undefined;
    let openTimer: number | undefined;
    const peer = new Peer();

    const checking: Record<string, TeleconsultoriaPresenceStatus> = {};
    for (const ref of refs) {
      checking[ref] = presencePeerIdFromPractitionerRef(ref)
        ? "checking"
        : "unknown";
    }
    setStatusByReference(checking);

    const apply = (next: Record<string, TeleconsultoriaPresenceStatus>) => {
      if (!cancelled) setStatusByReference(next);
    };

    const markUnknown = () => {
      const next: Record<string, TeleconsultoriaPresenceStatus> = {};
      for (const ref of refs) next[ref] = "unknown";
      apply(next);
    };

    const run = async () => {
      if (inFlight || cancelled || peer.destroyed || !peer.open) return;
      inFlight = true;
      try {
        const entries = await Promise.all(
          refs.map(async (ref) => {
            const peerId = presencePeerIdFromPractitionerRef(ref);
            if (!peerId) return [ref, "unknown"] as const;
            const status = await probeTeleconsultoriaPeer(
              peer,
              peerId,
              () => cancelled,
            );
            return [ref, status] as const;
          }),
        );
        if (cancelled || brokerFailed) return;
        apply(Object.fromEntries(entries));
      } finally {
        inFlight = false;
      }
    };

    const onOpen = () => {
      if (openTimer) window.clearTimeout(openTimer);
      void run();
      refreshTimer = window.setInterval(() => {
        void run();
      }, REFRESH_MS);
    };

    const onError = (err: PeerError<string>) => {
      if (!BROKER_ERROR_TYPES.has(err.type)) return;
      brokerFailed = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
      markUnknown();
    };

    peer.on("open", onOpen);
    peer.on("error", onError);
    openTimer = window.setTimeout(() => {
      if (cancelled || peer.open) return;
      markUnknown();
    }, OPEN_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (openTimer) window.clearTimeout(openTimer);
      if (refreshTimer) window.clearInterval(refreshTimer);
      peer.off("open", onOpen);
      peer.off("error", onError);
      if (!peer.destroyed) peer.destroy();
    };
  }, [referenceKey]);

  return statusByReference;
}
